import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RSD } from "../../lib/api";
import { fmtDuration, fmtTime, useMinuteTick } from "../../hooks/useShift";
import { ShiftMoneyCards, ShiftCoinsCard, ShiftSourceBreakdown } from "./ShiftCards";
import { StartShiftModal, EndShiftModal } from "./ShiftModals";
import { Play, Square, ChevronDown, ChevronUp, Clock, User } from "lucide-react";

function Dot({ open }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${open ? "bg-brand-green animate-pulse" : "bg-slate-300"}`} />;
}

/**
 * ONE shift control, two presentations. Both call the same backend service, so a shift
 * started in Vista is instantly the same shift in the Restaurant Panel and vice versa.
 * `variant="panel"` — full card for the Restaurant Panel.
 * `variant="vista"` — compact status strip for the fast walk-in tool.
 */
export default function ShiftControl({ rid, variant = "panel", canViewHistory = false, canViewFinancials = false, shiftState }) {
  const { shift, paymentMethod, busy, start, end } = shiftState;
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  useMinuteTick();

  const doStart = async (cash) => {
    const r = await start(cash);
    if (!r.ok) { toast.error(r.error); return; }
    setShowStart(false);
    toast.success(r.created ? "Smena je pokrenuta" : "Smena je već aktivna");
  };
  const doEnd = async (counted) => {
    const r = await end(counted);
    if (!r.ok) { toast.error(r.error); return; }
    setShowEnd(false);
    toast.success("Smena je završena");
  };

  const modals = (
    <>
      {showStart && <StartShiftModal paymentMethod={paymentMethod} busy={busy} showOpeningCash={canViewFinancials}
        onClose={() => setShowStart(false)} onStart={doStart} />}
      {showEnd && shift && <EndShiftModal shift={shift} paymentMethod={paymentMethod} busy={busy} showFinancials={canViewFinancials}
        onClose={() => setShowEnd(false)} onEnd={doEnd} />}
    </>
  );

  if (shift === undefined) {
    return <div data-testid="shift-loading" className="h-10 rounded-xl bg-slate-100 animate-pulse" />;
  }

  if (variant === "vista") {
    return (
      <div data-testid="shift-control-vista" className="flex items-center gap-2 flex-wrap">
        {shift ? (
          <>
            <span data-testid="vista-shift-active" className="flex items-center gap-2 rounded-xl bg-brand-green/10 px-3 py-2 text-sm font-black text-brand-green">
              <Dot open />SMENA U TOKU · {fmtTime(shift.started_at)}
            </span>
            <span className="text-xs text-slate-400 font-semibold">{fmtDuration(shift.started_at)}</span>
          </>
        ) : (
          <>
            <span data-testid="vista-shift-inactive" className="flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-sm font-black text-slate-500">
              <Dot />SMENA NIJE POKRENUTA
            </span>
            <button data-testid="vista-start-shift" onClick={() => setShowStart(true)}
              className="flex items-center gap-1.5 rounded-xl bg-brand-red hover:bg-brand-redHover text-white px-4 py-2 text-sm font-display font-black active:scale-[0.98] transition-transform">
              <Play size={14} />POČNI SMENU
            </button>
          </>
        )}
        {modals}
      </div>
    );
  }

  return (
    <div data-testid="shift-control-panel" className="bg-white rounded-2xl border p-3 mb-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="mr-auto">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Smena</p>
          <p data-testid="shift-status-label" className={`font-display font-black text-lg flex items-center gap-2 ${shift ? "text-brand-green" : "text-slate-500"}`}>
            <Dot open={!!shift} />{shift ? "U TOKU" : "NIJE POKRENUTA"}
            {shift && <span className="text-slate-900">· {fmtTime(shift.started_at)}</span>}
          </p>
          {shift && (
            <p className="text-xs text-slate-400 flex items-center gap-3 mt-0.5">
              <span className="flex items-center gap-1"><Clock size={12} />{fmtDuration(shift.started_at)}</span>
              <span className="flex items-center gap-1"><User size={12} />{shift.started_by_name || "—"}</span>
            </p>
          )}
        </div>
        {shift ? (
          <>
            {canViewFinancials && (
              <button data-testid="shift-details-toggle" onClick={() => setExpanded((v) => !v)}
                className="flex items-center gap-1.5 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-display font-black text-slate-700">
                DETALJI{expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
            )}
            <button data-testid="shift-end-open" onClick={() => setShowEnd(true)}
              className="flex items-center gap-1.5 rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-display font-black active:scale-[0.98] transition-transform">
              <Square size={13} />ZAVRŠI SMENU
            </button>
          </>
        ) : (
          <button data-testid="shift-start-open" onClick={() => setShowStart(true)}
            className="flex items-center gap-1.5 rounded-xl bg-brand-red hover:bg-brand-redHover text-white px-4 py-2.5 text-sm font-display font-black active:scale-[0.98] transition-transform">
            <Play size={14} />POČNI SMENU
          </button>
        )}
        {canViewHistory && (
          <button data-testid="shift-history-link" onClick={() => navigate(`/restaurant/${rid}/shifts`)}
            className="rounded-xl bg-white border-2 border-slate-200 px-4 py-2.5 text-sm font-display font-black text-slate-600">
            ISTORIJA
          </button>
        )}
      </div>

      {shift && expanded && canViewFinancials && (
        <div data-testid="shift-live-totals" className="mt-3 space-y-2">
          <ShiftMoneyCards shift={shift} paymentMethod={paymentMethod} />
          <ShiftCoinsCard shift={shift} />
          <ShiftSourceBreakdown shift={shift} />
          {paymentMethod !== "CARD" && (
            <p className="text-xs text-slate-400">
              Početno stanje kase: <b>{RSD(shift.opening_cash_amount)}</b> · Očekivano u kasi: <b>{RSD(shift.expected_drawer)}</b>
            </p>
          )}
        </div>
      )}
      {modals}
    </div>
  );
}
